import { confirmToast } from "../../utils/toast";
import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosConfig";

const emptyCountry = {
  CountryName: "",
  Country_ISO_Code_2: "",
  Country_ISO_Code_3: "",
  ISD_Code: ""
};
const emptyState = { StateName: "", CountryId: "", CountryName: "" };

const GeographyMaster = ({ type }) => {
  const isCountry = type === "country";
  const title = isCountry ? "Country Master" : "State Master";
  const endpoint = isCountry ? "/location-masters/countries" : "/location-masters/states";
  const idKey = isCountry ? "CountryId" : "StateId";
  const nameKey = isCountry ? "CountryName" : "StateName";

  const [activeTab, setActiveTab] = useState("list");
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(isCountry ? emptyCountry : emptyState);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const loadRows = async () => {
    try {
      const response = await axios.get(endpoint);
      setRows(response.data);
    } catch (err) {
      alert(`Failed to load ${title.toLowerCase()}: ${err.response?.data?.message || err.message}`);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await axios.get("/location-masters/countries");
      setCountries(response.data);
    } catch (err) {
      alert(`Failed to load countries: ${err.response?.data?.message || err.message}`);
    }
  };

  useEffect(() => {
    setRows([]);
    setForm(isCountry ? emptyCountry : emptyState);
    setEditingId(null);
    setSearch("");
    setCurrentPage(1);
    setActiveTab("list");
    loadRows();
    if (!isCountry) loadCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const resetForm = () => {
    setForm(isCountry ? emptyCountry : emptyState);
    setEditingId(null);
  };

  const openAdd = () => {
    resetForm();
    setActiveTab("form");
  };

  const findCountryByName = (countryName) => {
    const normalizedName = String(countryName || "").trim().toLowerCase();
    return countries.find((country) =>
      String(country.CountryName || "").trim().toLowerCase() === normalizedName
    );
  };

  const handleEdit = (row) => {
    setEditingId(row[idKey]);
    setForm(isCountry
      ? {
          CountryName: row.CountryName || "",
          Country_ISO_Code_2: (row.Country_ISO_Code_2 || "").trim(),
          Country_ISO_Code_3: (row.Country_ISO_Code_3 || "").trim(),
          ISD_Code: row.ISD_Code || ""
        }
      : {
          StateName: row.StateName || "",
          CountryId: row.CountryId || "",
          CountryName: row.CountryName || ""
        });
    setActiveTab("form");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = isCountry ? form : { ...form };
    if (!isCountry) {
      const country = findCountryByName(form.CountryName);
      if (!country) {
        alert("Please enter an existing country name.");
        return;
      }
      payload.CountryId = country.CountryId;
      delete payload.CountryName;
    }

    try {
      if (editingId) {
        await axios.put(`${endpoint}/${editingId}`, payload);
      } else {
        await axios.post(endpoint, payload);
      }
      alert(`${isCountry ? "Country" : "State"} ${editingId ? "updated" : "saved"} successfully`);
      resetForm();
      setActiveTab("list");
      loadRows();
    } catch (err) {
      alert(`Save failed: ${err.response?.data?.message || err.response?.data || err.message}`);
    }
  };

  const handleDelete = async (row) => {
    const label = row[nameKey];
    if (!await confirmToast(`Delete ${label}?`)) return;

    try {
      await axios.delete(`${endpoint}/${row[idKey]}`);
      if (editingId === row[idKey]) resetForm();
      loadRows();
    } catch (err) {
      alert(`Delete failed: ${err.response?.data?.message || err.response?.data || err.message}`);
    }
  };

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row[nameKey], row.Country_ISO_Code_2, row.Country_ISO_Code_3, row.ISD_Code, row.CountryName]
        .some((value) => String(value || "").toLowerCase().includes(term))
    );
  }, [rows, search, nameKey]);

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const safePage = Math.min(currentPage, Math.max(totalPages, 1));
  const firstIndex = (safePage - 1) * rowsPerPage;
  const currentRows = filteredRows.slice(firstIndex, firstIndex + rowsPerPage);

  return (
    <div className="container mt-3">
      <h4 className="fw-bold mb-3">{title}</h4>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === "list" ? "active" : ""}`} onClick={() => setActiveTab("list")}>
                {isCountry ? "Country" : "State"} List
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === "form" ? "active" : ""}`} onClick={openAdd}>
                {editingId ? `Edit ${isCountry ? "Country" : "State"}` : `Add ${isCountry ? "Country" : "State"}`}
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body">
          {activeTab === "list" && (
            <>
              <div className="mb-3">
                <input
                  type="search"
                  className="form-control"
                  style={{ maxWidth: 420 }}
                  placeholder={`Search ${isCountry ? "countries" : "states"}...`}
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 90 }}>ID</th>
                      <th>{isCountry ? "Country Name" : "State Name"}</th>
                      {isCountry && <th>ISO 2</th>}
                      {isCountry && <th>ISO 3</th>}
                      <th>{isCountry ? "ISD Code" : "Country"}</th>
                      <th className="text-center" style={{ width: 190 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.length === 0 ? (
                      <tr><td colSpan={isCountry ? "6" : "4"} className="text-center py-3">No {isCountry ? "countries" : "states"} found.</td></tr>
                    ) : currentRows.map((row) => (
                      <tr key={row[idKey]}>
                        <td>{row[idKey]}</td>
                        <td>{row[nameKey]}</td>
                        {isCountry && <td>{row.Country_ISO_Code_2}</td>}
                        {isCountry && <td>{row.Country_ISO_Code_3}</td>}
                        <td>{isCountry ? row.ISD_Code : row.CountryName}</td>
                        <td className="text-center">
                          <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(row)}>Edit</button>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span>Showing {firstIndex + 1} to {Math.min(firstIndex + rowsPerPage, filteredRows.length)} of {filteredRows.length} entries</span>
                  <div className="btn-group" role="group" aria-label="Pagination">
                    <button type="button" className="btn btn-outline-secondary btn-sm" disabled={safePage === 1} onClick={() => setCurrentPage(safePage - 1)}>Previous</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" disabled>{safePage} / {totalPages}</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" disabled={safePage === totalPages} onClick={() => setCurrentPage(safePage + 1)}>Next</button>
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === "form" && (
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{isCountry ? "Country Name" : "State Name"} *</label>
                  <input
                    className="form-control"
                    value={form[nameKey]}
                    onChange={(event) => setForm({ ...form, [nameKey]: event.target.value })}
                    required
                    autoFocus
                  />
                </div>

                {isCountry ? (
                  <>
                    <div className="col-md-2">
                      <label className="form-label fw-semibold">ISO Code 2</label>
                      <input
                        className="form-control text-uppercase"
                        value={form.Country_ISO_Code_2}
                        maxLength="2"
                        onChange={(event) => setForm({ ...form, Country_ISO_Code_2: event.target.value })}
                        placeholder="IN"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-semibold">ISO Code 3</label>
                      <input
                        className="form-control text-uppercase"
                        value={form.Country_ISO_Code_3}
                        maxLength="3"
                        onChange={(event) => setForm({ ...form, Country_ISO_Code_3: event.target.value })}
                        placeholder="IND"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-semibold">ISD Code</label>
                      <input
                        className="form-control"
                        value={form.ISD_Code}
                        maxLength="10"
                        onChange={(event) => setForm({ ...form, ISD_Code: event.target.value })}
                        placeholder="+91"
                      />
                    </div>
                  </>
                ) : (
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Country *</label>
                    <input
                      className="form-control"
                      value={form.CountryName}
                      onChange={(event) => setForm({ ...form, CountryName: event.target.value, CountryId: "" })}
                      placeholder="Enter Country Name"
                      required
                    />
                  </div>
                )}
              </div>

              <div className="mt-4 text-end">
                <button type="submit" className="btn btn-success me-2">{editingId ? "Update" : "Save"}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setActiveTab("list")}>Back to List</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeographyMaster;
